import { Button, Loading } from '@yimoka/antd';
import type { ButtonProps, SpinProps } from '@yimoka/antd';
import { observer, useInitStore } from '@yimoka/react';
import type { IStoreAPI, IStoreRunNow } from '@yimoka/store';
import type { HTMLAttributes } from 'react';
import { useEffect } from 'react';

import { authAccountGetImageCaptcha } from '@/entities/auth-account';
import type { IAuthAccountImageCaptcha } from '@/entities/auth-account.d';
import { useTranslation } from '@/locales/i18n';

export interface ImageCaptchaProps extends Omit<SpinProps, 'spinning'> {
  runNow?: IStoreRunNow;
  height?: number;
  onChange?: (id: string) => void;
  imgProps?: Omit<HTMLAttributes<HTMLImageElement>, 'src' | 'onClick'>;
  btnProps?: Omit<ButtonProps, 'disabled' | 'onClick'>;
  api?: IStoreAPI<Record<string, never>, IAuthAccountImageCaptcha>;
}

export const ImageCaptcha = observer((props: ImageCaptchaProps) => {
  const { t } = useTranslation();
  const { onChange, runNow = 'always', height = 32, imgProps, api, btnProps, ...args } = props;
  const { loading, response: { data } = {}, fetch } = useInitStore<Record<string, never>, IAuthAccountImageCaptcha>({
    options: { runNow },
    defaultValues: {},
    api: api ?? authAccountGetImageCaptcha,
  });
  const { id = '', image = '' } = data ?? {};

  useEffect(() => {
    onChange?.(id);
  }, [id, onChange]);

  const refreshImage = () => {
    if (!loading) void fetch();
  };

  return (
    <Loading {...args} spinning={loading}>
      {image ? (
        <img
          alt={t('Account:captcha.imageAlt')}
          height={height}
          style={{ position: 'relative', top: 1, display: 'block', cursor: 'pointer' }}
          {...imgProps}
          src={image}
          onClick={refreshImage}
        />
      ) : (
        <Button
          ghost
          disabled={loading}
          type="primary"
          onClick={refreshImage}
          {...btnProps}
        >
          {t('Account:captcha.getImage')}
        </Button>
      )}
    </Loading>
  );
});
